import { Button } from 'antd';
import dayjs from 'dayjs';
import 'jspdf-autotable';
import { useEffect, useRef, useState } from 'react';
import Barcode from 'react-barcode';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchBundleInWardById } from '../../slices/bundleinword';

function PrintAddBundleInWard() {
  const { id } = useParams();
  const printRef = useRef(null);
  const dispatch = useDispatch();
  const [bundleData, setBundleData] = useState([]);

  useEffect(() => {
    if (id) {
      dispatch(fetchBundleInWardById({ id })).then(response => {
        if (response.payload) {
          setBundleData(response.payload.data);
        }
      });
    }
  }, [dispatch, id]);

  // Add print styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
       @media print {
          @page {
            size: 100mm 50mm landscape;
            margin: 0;
          }
          
         html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: white;
          font-family: 'Raleway', sans-serif !important;
          transform: none !important; /* Prevent browser scaling */
        }
          .no-print {
            display: none !important;
          }
          
        body > div, #print-area, .barcode-container {
          transform: none !important;
          transform-origin: center !important;
          rotate: 180deg !important; /* Counteract printer's 180° rotation */
        }
          
          #print-area {
            width: 384px !important; /* 102mm ≈ 384px at 96 DPI */
            height: 216px !important; /* 57mm ≈ 216px at 96 DPI */
            padding: 0 !important;
           
            background: white !important;
            overflow: hidden !important;
            page-break-inside: avoid;
            page-break-after: avoid;
            position: relative;
          margin-top:50px !important;
            font-family: 'Raleway', sans-serif !important;
            
          }
          
        .whole-container {
         
          padding: 10px;
          box-sizing: border-box;
          transform: rotate(180deg) !important; /* Ensure content aligns with printer */
            transform-origin: center !important;
        }
          /* Ensure barcode prints correctly */
        .barcode-container {
          transform: rotate(180deg) !important; /* Match printer's rotation */
          transform-origin: center !important;
        }
  
        }
  
      `;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const paddedBundle = bundleData.bundle_no
    ? String(bundleData.bundle_no).padStart(7, '0')
    : '';
  const printAreaStyle = {
    width: '400px',
    height: '180px',
    padding: '0',
    margin: '40px auto',
    background: 'white',
    position: 'relative',
    overflow: 'hidden',
  };

  const tableStyle = {
    marginTop: '40px',
    width: '100%',
    tableLayout: 'fixed',
  };

  const textStyle = {
    fontSize: '12px',
    lineHeight: '1',
  };

  const imageStyle = {
    width: '80px',
    display: 'inline-block',
    marginLeft: '5px',
    marginRight: '50px',
  };

  const barcodeContainerStyle = {
    position: 'absolute',
    transform: 'rotate(270deg)',
    right: '-25px',
    top: '70px',
  };
  return (
    <div>
      <div
        id="print-area"
        ref={printRef}
        style={printAreaStyle}
        className="whole-container"
      >
        <table style={tableStyle}>
          <tbody>
            <tr>
              <td
                style={{
                  paddingLeft: '20px',
                  width: '40%',
                  verticalAlign: 'top',
                }}
              >
                <div style={textStyle}>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Bundle No: <b>{bundleData.bundle_no || 'N/A'}</b>
                  </div>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    WorkOrder No: <b>{bundleData.workorder_no || 'N/A'}</b>
                  </div>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Date:{' '}
                    <b>
                      {bundleData.packing_date
                        ? dayjs(bundleData.packing_date).format('DD/MM/YYYY')
                        : 'N/A'}
                    </b>
                  </div>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Profile No:{' '}
                    <b>
                      {bundleData.die_number || 'N/A'}
                      {bundleData.customer_reference_no
                        ? ` (${bundleData.customer_reference_no})`
                        : ''}
                    </b>
                  </div>

                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Alloy & Temper:{' '}
                    <b>
                      {bundleData.alloy_code || 'N/A'} {bundleData.temper || ''}
                    </b>
                  </div>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Length(mm): <b>{bundleData.length || 'N/A'}</b> &nbsp;&nbsp;
                    Pcs: <b>{bundleData.pieces || 'N/A'}</b>
                  </div>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Nt. Wt:{' '}
                    <b>
                      {bundleData.net_weight
                        ? Number(bundleData.net_weight)?.toFixed(3)
                        : 'N/A'}{' '}
                      Kg.
                    </b>
                  </div>
                  <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                    Gross. Wt:{' '}
                    <b>
                      {bundleData.gross_weight
                        ? Number(bundleData.gross_weight)?.toFixed(3)
                        : 'N/A'}{' '}
                      Kg.{' '}
                    </b>
                  </div>
                  {bundleData.workorder_type === 'Job Work' &&
                    Array.isArray(bundleData.job_work?.jobworks) &&
                    bundleData.job_work.jobworks.length > 0 && (
                      <div style={{ marginBottom: '2px', fontWeight: '500' }}>
                        Mill Finish:{' '}
                        <b>
                          {(() => {
                            const jobWorkArr = bundleData.job_work.jobworks;
                            let resultArr = [];
                            jobWorkArr.forEach(jw => {
                              if (jw === 'Surface treatment') {
                                const surfaceArr = [];
                                if (bundleData.job_work.anodising) surfaceArr.push('Anodising');
                                if (bundleData.job_work.powder_coating) surfaceArr.push('Powder Coating');
                                if (bundleData.job_work.pvdf) surfaceArr.push('PVDF');
                                if (surfaceArr.length > 0) {
                                  resultArr = resultArr.concat(surfaceArr);
                                }
                              } else if (jw === 'Engineering') {
                                const engArr = [];
                                if (bundleData.job_work.cutting) engArr.push('Cutting');
                                if (bundleData.job_work.machining) engArr.push('Machining');
                                if (bundleData.job_work.deburring) engArr.push('Deburring');
                                if (engArr.length > 0) {
                                  resultArr = resultArr.concat(engArr);
                                }
                              } else if (jw === 'Out Source') {
                                resultArr.push('O');
                              } else if (jw.toLowerCase() === 'laser marking') {
                                resultArr.push('LM');
                              } else {
                                resultArr.push(jw);
                              }
                            });
                            return resultArr.join(', ');
                          })()}
                        </b>
                      </div>
                    )
                  }
                </div>
              </td>
              <td
                style={{ paddingLeft: '5px', width: '30%', verticalAlign: 'top' }}
              >
                {bundleData?.die_diagram && (
                  <img
                    src={bundleData.die_diagram}
                    alt="Die Diagram"
                    style={imageStyle}
                  />
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <div style={barcodeContainerStyle}>
          <Barcode
            value={paddedBundle || '0000000'}
            width={0.8}
            height={35}
            displayValue={false}
            background=""
            lineColor="#000"
          />
        </div>
      </div>

      {/* Print Button */}
      <div className="no-print" style={{ textAlign: 'center', marginTop: 20 }}>
        <Button onClick={handlePrint} type="primary">
          Print
        </Button>
      </div>
    </div>
  );
}

export default PrintAddBundleInWard;
